import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

export type Role = 'SYS_ADMIN' | 'PRESIDENT' | 'VICE_PRESIDENT'
  | 'WDC_PRESIDENT' | 'WDC_VICE_PRESIDENT' | 'WDC_MEMBER'
  | 'SECRETARY_GENERAL' | 'MEMBER' | 'BUREAU_STAFF' | 'VIEWER';
export interface Me {
  id: string;
  email: string;
  name: string;
  nameDv?: string | null;
  role: Role;
  orgId: string | null;
  orgName?: string | null;
  isOrgAdmin: boolean;
  designationDv?: string | null;
  designationEn?: string | null;
  isDemo?: boolean;
}

interface AuthResponse { accessToken: string; refreshToken: string; user: Me }

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: Me | null;
  login: (email: string, password: string) => Promise<void>;
  startDemo: () => Promise<void>;
  registerViaInvite: (inviteToken: string, password: string, name: string) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  hydrateMe: () => Promise<void>;
  ssoBootstrap: (force?: boolean) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      login: async (email, password) => {
        const r = await axios.post<AuthResponse>('/api/auth/login', { email, password });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      startDemo: async () => {
        const r = await axios.post<AuthResponse>('/api/demo/start', {});
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      registerViaInvite: async (inviteToken, password, name) => {
        const r = await axios.post<AuthResponse>('/api/auth/register', { inviteToken, password, name });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      refresh: async () => {
        const t = get().refreshToken;
        if (!t) throw new Error('no refresh token');
        const r = await axios.post<AuthResponse>('/api/auth/refresh', { refreshToken: t });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      logout: async () => {
        const t = get().refreshToken;
        // withCredentials so the parent-domain wap_sso cookie rides along and the
        // server can revoke the Office shared session + clear the cookie.
        try {
          await axios.post('/api/auth/logout', { refreshToken: t ?? undefined }, { withCredentials: true });
        } catch { /* non-fatal — still clear local state + navigate */ }
        set({ accessToken: null, refreshToken: null, user: null });
        // Hard nav to /login. Clearing the token isn't enough: route guards run
        // in beforeLoad on navigation, and zustand state changes alone won't
        // trigger them. A hard nav also drops the socket connection + query
        // cache so the next user starts fresh.
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      },
      hydrateMe: async () => {
        const a = get().accessToken;
        if (!a) return;
        try {
          const r = await axios.get<{ user: Me }>('/api/users/me', { headers: { Authorization: `Bearer ${a}` } });
          // Guard against a session switch mid-flight (e.g. SSO handoff): only
          // apply if the token we used is still current, else we'd clobber the
          // newly switched-in user.
          if (get().accessToken !== a) return;
          set({ user: r.data.user });
        } catch { /* swallow — refresh will fire on next call */ }
      },
      // Ambient-SSO cold-boot: with no local session, ask the server to resolve
      // the parent-domain wap_sso cookie into a session. withCredentials so the
      // cookie is sent. Returns true if a session was established (store updated
      // exactly like login), false on 204/absent/error → render login as normal.
      ssoBootstrap: async (force = false) => {
        if (!force && get().accessToken) return true;
        try {
          const r = await axios.post<AuthResponse | ''>('/api/auth/sso-bootstrap', {}, { withCredentials: true });
          if (r.status === 204 || !r.data || !(r.data as AuthResponse).accessToken) return false;
          const d = r.data as AuthResponse;
          set({ accessToken: d.accessToken, refreshToken: d.refreshToken, user: d.user });
          return true;
        } catch {
          return false;
        }
      },
    }),
    { name: 'wapmeet.auth' },
  ),
);
